import React from "react";
import type { ImportAttributes } from "../../../types";
import { DataList, ListColumn, ListAction, Icons } from "../../shared";
import { formatDateTime } from "../../../utils/dateUtils";

const TYPE_LABELS: Record<string, string> = {
  transaction: "Lançamentos",
  category: "Categorias",
  bankAccount: "Contas",
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function importCreatedAtToTime(item: ImportAttributes): number | null {
  const raw = item.createdAt;
  if (raw == null) return null;
  const d = typeof raw === "string" ? new Date(raw) : raw instanceof Date ? raw : new Date(String(raw));
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

/** Excluir só se a importação foi criada há menos de 7 dias. */
function canDeleteImportByAge(item: ImportAttributes): boolean {
  const created = importCreatedAtToTime(item);
  if (created == null) return false;
  const ageMs = Date.now() - created;
  return ageMs >= 0 && ageMs < SEVEN_DAYS_MS;
}

interface ImportsListViewProps {
  imports: ImportAttributes[];
  onDelete: (id: number) => void;
  loading?: boolean;
}

export const ImportsListView: React.FC<ImportsListViewProps> = ({
  imports,
  onDelete,
  loading = false,
}) => {
  const columns: ListColumn<ImportAttributes>[] = [
    {
      key: "id",
      label: "Id",
      width: "1fr",
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-slate-400 font-mono">{item.id}</span>
      ),
    },
    {
      key: "type",
      label: "Tipo",
      width: "2fr",
      render: (item) => (
        <span className="text-sm font-medium text-gray-900 dark:text-slate-200">
          {TYPE_LABELS[item.type] ?? item.type}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Data",
      width: "2fr",
      render: (item) => {
        const raw = item.createdAt;
        const dateStr =
          raw == null
            ? null
            : typeof raw === "string"
              ? raw
              : (raw as Date).toISOString?.() ?? String(raw);
        return (
          <span className="text-sm text-gray-600 dark:text-slate-400">
            {formatDateTime(dateStr)}
          </span>
        );
      },
    },
    {
      key: "deletedAt",
      label: "Excluído em",
      width: "2fr",
      render: (item) => {
        const raw = item.deletedAt;
        if (raw == null) return <span className="text-sm text-gray-400 dark:text-slate-500">—</span>;
        const dateStr =
          typeof raw === "string"
            ? raw
            : (raw as Date).toISOString?.() ?? String(raw);
        return (
          <span className="text-sm text-gray-600 dark:text-slate-400">
            {formatDateTime(dateStr)}
          </span>
        );
      },
    },
  ];

  const actions: ListAction<ImportAttributes>[] = [
    {
      label: "Excluir",
      icon: <Icons.Delete />,
      onClick: (item) => onDelete(item.id),
      visible: (item) =>
        item.type === "transaction" && !item.isDeleted && canDeleteImportByAge(item),
      className:
        "p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200",
      title: "Excluir importação (só nos primeiros 7 dias após a criação)",
      disabled: () => loading,
    },
  ];

  return (
    <DataList<ImportAttributes>
      data={imports}
      columns={columns}
      actions={actions}
      keyExtractor={(item) => item.id}
      emptyMessage="Nenhuma importação encontrada"
      loading={loading}
    />
  );
};
